import { useEffect, useState } from "react";
import { getTodayDashboard } from "../../lib/crm-api";
import type { Action, TodayPayload } from "../../lib/crm-types";

export default function CrmToday() {
  const [data, setData] = useState<TodayPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTodayDashboard()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center font-mono text-sm text-concrete">
        Chargement…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center font-mono text-sm text-concrete">
        Erreur de chargement.
      </div>
    );
  }

  const todayStr = new Date().toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const totalMails21d = data.histograms.mails_21d.reduce(
    (s, d) => s + d.mails_sent_today,
    0,
  );

  return (
    <div className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="bg-charcoal text-ivory px-10 py-5 flex items-baseline justify-between">
        <div className="flex items-baseline gap-4">
          <h1 className="font-sans text-2xl font-bold tracking-tight">
            llyll · CRM
          </h1>
          <nav className="flex gap-4 font-mono text-xs uppercase text-stone">
            <span className="text-lemonade">Aujourd'hui</span>
            <span className="opacity-40">Estrellas</span>
            <span className="opacity-40">Touchpoints</span>
            <span className="opacity-40">Batches</span>
            <span className="opacity-40">Leads</span>
            <span className="opacity-40">Réglages</span>
          </nav>
        </div>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[10px] uppercase text-stone">
            {data.source === "mock" ? "▢ MOCK DATA" : "● SUPABASE"}
          </span>
          <span className="font-mono text-[10px] uppercase text-stone">
            {todayStr}
          </span>
        </div>
      </header>

      <main className="px-10 py-8 max-w-[1200px] mx-auto">
        <div className="mb-6">
          <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
            Aujourd'hui
          </h2>
        </div>

        {/* Hero scores */}
        <section className="grid grid-cols-2 gap-4 mb-6">
          <ScoreCard
            label="Estrellas"
            score={data.scores.estrella.estrella_score}
            total={data.scores.estrella.estrella_total}
            overdue={data.scores.estrella.estrella_overdue}
            sublabel="en retard"
            histogram={data.histograms.scores_14d.map((d) => ({
              label: d.digest_date,
              value: d.estrella_score,
            }))}
            accent
          />
          <ScoreCard
            label="Non-Estrellas"
            score={data.scores.non_estrella.non_estrella_score}
            total={data.scores.non_estrella.non_estrella_total}
            overdue={
              data.scores.non_estrella.non_estrella_total -
              data.scores.non_estrella.recently_touched
            }
            sublabel="à relancer"
            histogram={data.histograms.scores_14d.map((d) => ({
              label: d.digest_date,
              value: d.non_estrella_score,
            }))}
          />
        </section>

        {/* À rattraper */}
        {data.actions.late.length > 0 && (
          <section className="mb-6">
            <h3 className="font-sans text-lg font-bold mb-3 text-charcoal flex items-baseline gap-3">
              <span>⚠ À rattraper en priorité</span>
              <span className="font-mono text-xs text-concrete font-normal">
                {data.actions.late.length} action
                {data.actions.late.length > 1 ? "s" : ""}
              </span>
            </h3>
            <ul className="flex flex-col gap-2">
              {data.actions.late.map((a) => (
                <ActionRow key={a.id} action={a} />
              ))}
            </ul>
          </section>
        )}

        {/* Due today */}
        {data.actions.due_today.length > 0 && (
          <section className="mb-6">
            <h3 className="font-sans text-lg font-bold mb-3 text-charcoal flex items-baseline gap-3">
              <span>Aujourd'hui</span>
              <span className="font-mono text-xs text-concrete font-normal">
                {data.actions.due_today.length} action
                {data.actions.due_today.length > 1 ? "s" : ""}
              </span>
            </h3>
            <ul className="flex flex-col gap-2">
              {data.actions.due_today.map((a) => (
                <ActionRow key={a.id} action={a} />
              ))}
            </ul>
          </section>
        )}

        {/* Mails sent histogram */}
        <section className="mb-12">
          <h3 className="font-sans text-lg font-bold mb-3 text-charcoal flex items-baseline gap-3">
            <span>Mails envoyés — 21 derniers jours</span>
            <span className="font-mono text-xs text-concrete font-normal">
              total : {totalMails21d}
            </span>
          </h3>
          <div className="bg-white border border-stone rounded-lg p-6">
            <Histogram
              data={data.histograms.mails_21d.map((d) => ({
                label: d.digest_date,
                value: d.mails_sent_today,
              }))}
              height={120}
            />
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center font-mono text-[10px] uppercase text-concrete opacity-50 py-4 border-t border-stone">
          llyll CRM · v0.1 · {data.source === "mock" ? "mock data" : "supabase live"}
        </footer>
      </main>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

function ScoreCard({
  label,
  score,
  total,
  overdue,
  sublabel,
  histogram,
  accent = false,
}: {
  label: string;
  score: number;
  total: number;
  overdue: number;
  sublabel: string;
  histogram: { label: string; value: number }[];
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-6 border ${
        accent
          ? "bg-charcoal text-ivory border-charcoal"
          : "bg-white text-charcoal border-stone"
      }`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <span
          className={`font-mono text-[11px] uppercase tracking-wider ${
            accent ? "text-lemonade" : "text-concrete"
          }`}
        >
          {label}
        </span>
        <span
          className={`font-mono text-[10px] uppercase ${
            accent ? "text-stone" : "text-ash"
          }`}
        >
          {total} leads
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-sans text-5xl font-black tracking-tight">
          {score}
          <span className="text-2xl">%</span>
        </span>
        {overdue > 0 && (
          <span
            className={`font-mono text-xs ${
              accent ? "text-lemonade" : "text-charcoal"
            }`}
          >
            · {overdue} {sublabel}
          </span>
        )}
      </div>
      <Histogram data={histogram} height={48} accentOnAccent={accent} />
    </div>
  );
}

function ActionRow({ action }: { action: Action }) {
  const borderCls =
    action.urgency === "late_bad"
      ? "border-l-[3px] border-l-red-600"
      : action.urgency === "late_mid"
        ? "border-l-[3px] border-l-orange-400"
        : "";

  return (
    <li
      className={`bg-white border border-stone rounded-lg px-4 py-3 grid grid-cols-[auto_1fr_auto] gap-3 items-center ${borderCls}`}
    >
      <span
        className={`w-5 h-5 rounded-full border-[1.5px] ${
          action.is_estrella
            ? "border-lemonade bg-lemonade"
            : "border-stone bg-white"
        }`}
        aria-label={action.is_estrella ? "Estrella" : "Non-Estrella"}
      />
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="font-semibold text-sm flex items-baseline gap-2 flex-wrap">
          <span className="text-charcoal">{action.full_name}</span>
          <span
            className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded text-white"
            style={{ backgroundColor: action.category_color }}
          >
            {action.category_name}
          </span>
        </div>
        <div className="text-[13px] text-concrete leading-snug">
          {action.what}
        </div>
        <div className="flex gap-3 font-mono text-[10px] text-concrete uppercase mt-1">
          <span>
            Dernier :{" "}
            {action.last_contact_at?.slice(0, 10) ?? "—"}
          </span>
          {action.urgency === "late_bad" && (
            <span className="bg-red-600 text-white px-1.5 rounded font-bold">
              TRÈS EN RETARD · +{action.days_late}J
            </span>
          )}
          {action.urgency === "late_mid" && (
            <span className="bg-orange-100 text-orange-700 px-1.5 rounded font-bold">
              EN RETARD · +{action.days_late}J
            </span>
          )}
          {action.urgency === "due_today" && (
            <span className="bg-lemonade text-charcoal px-1.5 rounded font-bold">
              DUE TODAY
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-1 text-xs font-mono">
        <span className="opacity-40">[mail] [WA] [brief]</span>
      </div>
    </li>
  );
}

function Histogram({
  data,
  height = 60,
  accentOnAccent = false,
}: {
  data: { label: string; value: number }[];
  height?: number;
  accentOnAccent?: boolean;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex items-end gap-1" style={{ height: `${height}px` }}>
      {data.map((d, i) => {
        const pct = (d.value / max) * 100;
        const bg = accentOnAccent
          ? "bg-lemonade"
          : pct < 50
            ? "bg-red-300"
            : pct < 80
              ? "bg-orange-300"
              : "bg-charcoal";
        return (
          <div
            key={i}
            className="flex-1 relative group"
            title={`${d.value} · ${d.label}`}
          >
            <div
              className={`${bg} rounded-t-sm`}
              style={{ height: `${Math.max(2, pct)}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}
